// =============================================================================
// MyComplianceScreen — /my-compliance
// =============================================================================
// A worker's own readiness, for the worker. Same component the director sees, so
// there is one version of the truth and no argument at a site gate about what
// somebody is missing.
//
// Written for a phone in the sun on a tower site, because that is where it gets
// read: the verdict first, then what needs doing, then everything else. No
// tables, no filters, nothing to configure.
//
// Nothing here needs a permission check. v_employee_requirements is
// security_invoker, so the database returns this person's rows and nobody
// else's - the screen simply asks for "mine".
// =============================================================================

import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, AlertTriangle } from "lucide-react";
import { useRequireAuth } from "@/lib/auth";
import { Card, EmptyState } from "@/components/ui";
import PersonReadinessPanel from "./PersonReadinessPanel";

export default function MyComplianceScreen() {
  const user = useRequireAuth();
  if (!user) return null;

  const employeeId = (user as any).employee_id as string | null;

  if (!employeeId) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <EmptyState
            icon={AlertTriangle}
            title="No employee record linked"
            description="This login isn't linked to an employee record, so there are no tickets to check. Ask the office to link it."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto pb-20">
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14} /> My portal
      </Link>

      <div className="mb-5">
        <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
          <ShieldCheck size={20} className="text-accent" /> My tickets
        </h1>
        <p className="text-text-muted text-[13px] mt-1">
          What you need for the work you do, what's current, and what's running out.
          Anything showing as missing or expired needs sorting before you're on site.
        </p>
      </div>

      <PersonReadinessPanel employeeId={employeeId} />

      <div className="mt-5 text-[12px] text-text-muted">
        Something wrong here? If you hold a ticket that shows as missing, it probably
        hasn't been uploaded yet — send it to the office and it'll appear once it's on file.
      </div>
    </div>
  );
}
